import { loadStripe } from '@stripe/stripe-js'
import { supabase } from './supabase'

// Initialize Stripe
const stripePublishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY

// Only create Stripe instance if key is available
export const stripePromise = stripePublishableKey
  ? loadStripe(stripePublishableKey)
  : null

// Stripe subscription plans
export const SUBSCRIPTION_PLANS = {
  FREE: {
    id: 'free',
    name: 'Free',
    price: 0,
    interval: 'month',
    quotaLimit: 10,
    features: [
      '10 quotes per month',
      'Basic email notifications',
      'Digital signatures',
      'Dashboard analytics'
    ]
  },
  SOLO: {
    id: 'price_SOLO_MONTHLY', // TODO: Replace with actual Stripe price ID after creating in Stripe dashboard
    name: 'Solo',
    price: 39,
    interval: 'month',
    quotaLimit: null, // Unlimited
    features: [
      'Unlimited quotes',
      'Email notifications',
      'Digital signatures',
      'Dashboard analytics',
      'Custom branding',
      'PDF generation',
      'Priority support'
    ]
  },
  CREW: {
    id: 'price_CREW_MONTHLY', // TODO: Replace with actual Stripe price ID after creating in Stripe dashboard
    name: 'Crew (2-8 users)',
    price: 79,
    pricePerExtraUser: 10,
    interval: 'month',
    quotaLimit: null, // Unlimited
    minUsers: 2,
    maxUsers: 8,
    features: [
      'Unlimited quotes',
      'Team collaboration (2-8 users)',
      '$10/month per additional user',
      'All Solo features',
      'Team management',
      'Shared templates',
      'Advanced analytics'
    ]
  },
  ANNUAL: {
    id: 'price_ANNUAL', // TODO: Replace with actual Stripe price ID after creating in Stripe dashboard
    name: 'Annual Plan',
    price: 390, // $39/mo * 10 months (2 months free)
    interval: 'year',
    quotaLimit: null, // Unlimited
    savingsMonths: 2,
    features: [
      'All Solo features',
      '2 months free (save $78)',
      'Free template setup on Zoom',
      'Annual billing',
      'Priority support'
    ]
  }
}

// Stripe service functions
export const stripeService = {
  // Create checkout session for subscription
  createCheckoutSession: async (priceId) => {
    try {
      if (!stripePublishableKey) {
        throw new Error('Stripe is not configured. Please add REACT_APP_STRIPE_PUBLISHABLE_KEY to your environment variables.')
      }

      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.access_token) {
        throw new Error('Authentication required')
      }

      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const response = await fetch(`${apiUrl}/api/stripe/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          priceId,
          successUrl: `${window.location.origin}/dashboard?subscription=success`,
          cancelUrl: `${window.location.origin}/pricing?subscription=canceled`
        })
      })

      if (!response.ok) {
        throw new Error('Failed to create checkout session')
      }

      const { sessionId } = await response.json()

      const stripe = await stripePromise
      const { error } = await stripe.redirectToCheckout({ sessionId })

      if (error) {
        throw error
      }
    } catch (error) {
      console.error('Stripe checkout error:', error)
      throw error
    }
  },

  // Create customer portal session
  createPortalSession: async () => {
    try {
      if (!stripePublishableKey) {
        throw new Error('Stripe is not configured. Please add REACT_APP_STRIPE_PUBLISHABLE_KEY to your environment variables.')
      }

      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.access_token) {
        throw new Error('Authentication required')
      }

      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const response = await fetch(`${apiUrl}/api/stripe/create-portal-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({})
      })

      if (!response.ok) {
        throw new Error('Failed to create portal session')
      }

      const { url } = await response.json()
      window.location.href = url
    } catch (error) {
      console.error('Stripe portal error:', error)
      throw error
    }
  },

  // Get subscription status
  getSubscriptionStatus: (profile) => {
    if (!profile) return null
    
    const { subscription_status, trial_ends_at } = profile
    
    if (subscription_status === 'trialing') {
      const trialEndsAt = new Date(trial_ends_at)
      const now = new Date()
      const daysLeft = Math.ceil((trialEndsAt - now) / (1000 * 60 * 60 * 24))
      
      return {
        status: 'trialing',
        daysLeft: Math.max(0, daysLeft),
        isExpired: daysLeft <= 0
      }
    }
    
    return {
      status: subscription_status,
      daysLeft: null,
      isExpired: subscription_status === 'canceled' || subscription_status === 'past_due'
    }
  },

  // Check if user has access to features
  hasFeatureAccess: (profile, feature) => {
    if (!profile) return false
    
    const subscriptionStatus = stripeService.getSubscriptionStatus(profile)
    
    // During trial or active subscription, user has access
    if (subscriptionStatus.status === 'trialing' && !subscriptionStatus.isExpired) {
      return true
    }
    
    if (subscriptionStatus.status === 'active') {
      return true
    }
    
    return false
  }
}
